import { useTranslation } from "@helpers/useTranslation";
import { useEventNavigate } from "@helpers/useEventNavigate";
import { FC, useMemo } from "react";

type AgendaSlot = {
  id: string;
  time: string;
  title: string;
};

type ExcursionGroup = {
  id: string;
  time: string;
  label: string;
};

const ONNINEN_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/onninen.svg`;

/** Throwaway Onninen excursion groups (LT). */
const EXCURSION_GROUPS_LT: ExcursionGroup[] = [
  { id: "g1", time: "13:00 val.", label: "1 grupė (LT) → juoda" },
  { id: "g2", time: "13:10 val.", label: "2 grupė (LT) → balta" },
  { id: "g3", time: "13:20 val.", label: "3 grupė (EN) → sidabrinė" },
  { id: "g4", time: "13:30 val.", label: "4 grupė (LT) → auksinė" },
  { id: "g5", time: "13:40 val.", label: "5 grupė (LT) → oranžinė" },
  { id: "g6", time: "13:50 val.", label: "6 grupė (LT) → mėlyna" },
];

const EXCURSION_GROUPS_EN: ExcursionGroup[] = [
  { id: "g1", time: "13:00 p.m.", label: "Group 1 (LT) → black" },
  { id: "g2", time: "13:10 p.m.", label: "Group 2 (LT) → white" },
  { id: "g3", time: "13:20 p.m.", label: "Group 3 (EN) → silver" },
  { id: "g4", time: "13:30 p.m.", label: "Group 4 (LT) → gold" },
  { id: "g5", time: "13:40 p.m.", label: "Group 5 (LT) → orange" },
  { id: "g6", time: "13:50 p.m.", label: "Group 6 (LT) → blue" },
];

/** Throwaway Onninen day programme (LT). */
const EVENT_PROGRAMME_LT: AgendaSlot[] = [
  { id: "lt-1", time: "12:00 val.", title: "Parodos atidarymas, dalyvių registracija." },
  { id: "lt-2", time: "12:30 val.", title: "Pietūs." },
  {
    id: "lt-3",
    time: "12:00 val.",
    title:
      "Gamintojų stendų lankymas, dalyvavimas žaidimuose, ekskursija po naują Onninen logistikos centrą.",
  },
  { id: "lt-4", time: "13:30 val.", title: "Konferencijos atidarymas." },
  { id: "lt-5", time: "13:40 val.", title: "Konferencija." },
  { id: "lt-6", time: "17:00 val.", title: "Oficiali naujo Onninen logistikos centro atidarymo ceremonija." },
  { id: "lt-7", time: "17:20 val.", title: "Loterijos laimėtojų apdovanojimai." },
  { id: "lt-8", time: "18:00 val.", title: "Vakarienė." },
  { id: "lt-9", time: "21:15 val.", title: "Autobusai išvyksta į Vilnių ir Klaipėdą." },
];

/** Throwaway Onninen day programme (EN). */
const EVENT_PROGRAMME_EN: AgendaSlot[] = [
  { id: "en-1", time: "12:00 p.m.", title: "Exhibition opening, participant registration." },
  { id: "en-2", time: "12:30 p.m.", title: "Lunch." },
  {
    id: "en-3",
    time: "12:00 p.m.",
    title: "Visiting manufacturers’ booths, participating in games, tour of the new Onninen logistics center.",
  },
  { id: "en-4", time: "1:30 p.m.", title: "Conference opening." },
  { id: "en-5", time: "1:40 p.m.", title: "Conference." },
  { id: "en-6", time: "5:00 p.m.", title: "Official opening ceremony for the new Onninen logistics center." },
  { id: "en-7", time: "5:20 p.m.", title: "Awarding of prizes to the raffle winners." },
  { id: "en-8", time: "6:00 p.m.", title: "Dinner." },
  { id: "en-9", time: "7:00 p.m.", title: "Concert and evening entertainment." },
  { id: "en-10", time: "9:00 p.m.", title: "End of the event." },
];

const isLithuanianUi = (lang: string): boolean => lang === "lt" || lang.startsWith("lt-");

export const AgendaFirst: FC = () => {
  const { i18n } = useTranslation();
  const navigate = useEventNavigate();

  const locale = i18n.language || "en";
  const isLt = isLithuanianUi(locale);

  const excursionHeading = isLt ? "Ekskursijų grupės" : "Excursion groups";
  const programmeHeading = isLt ? "Konferencijos programa" : "Event programme";
  const continueLabel = isLt ? "Tęsti" : "Continue";

  const excursionGroups = useMemo(() => (isLt ? EXCURSION_GROUPS_LT : EXCURSION_GROUPS_EN), [isLt]);
  const slots = useMemo(() => (isLt ? EVENT_PROGRAMME_LT : EVENT_PROGRAMME_EN), [isLt]);

  return (
    <div className="flex h-screen flex-col">
      <main className="overflow-y-auto ">
        <div className="mx-auto flex max-w-xl flex-col px-6 mb-24 pt-6">
          <div className="mb-8 flex justify-center py-6">
            <img
              src={ONNINEN_LOGO_SRC}
              alt="Onninen"
              className="h-auto max-h-14 w-auto max-w-[min(100%,220px)] object-contain object-center"
            />
          </div>

          <section className="mb-14">
            <h2 className="mb-8 text-center text-2xl font-semibold tracking-tight sm:text-2xl">
              {excursionHeading}
            </h2>
            <ol className="space-y-3">
              {excursionGroups.map((row) => (
                <li key={row.id} className="flex gap-3 text-left sm:gap-4">
                  <span className="w-[5.5rem]  shrink-0 font-mono text-sm font-semibold tabular-nums text-primary sm:w-16 sm:text-base">
                    {row.time}
                  </span>
                  <p className="min-w-0 flex-1 text-sm leading-snug text-base-content sm:text-base">
                    {row.label}
                  </p>
                </li>
              ))}
            </ol>
          </section>

          <section className="mb-24">
            <h2 className="mb-8 text-center text-2xl font-semibold tracking-tight sm:text-2xl">
              {programmeHeading}
            </h2>
            <ol className="space-y-6">
              {slots.map((slot) => (
                <li key={slot.id} className="flex gap-4 text-left sm:gap-6">
                  <span className="w-[5.5rem] shrink-0 font-mono text-sm font-semibold tabular-nums text-primary sm:w-28 sm:text-base">
                    {slot.time}
                  </span>
                  <p className="min-w-0 flex-1 text-base leading-snug text-base-content">{slot.title}</p>
                </li>
              ))}
            </ol>
          </section>

          <div className="pb-8">
            <button
              type="button"
              className="btn btn-primary btn-block w-full text-base font-semibold"
              onClick={() => navigate("/select")}
            >
              {continueLabel}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};
